import React, { useState, useEffect } from 'react';
import './HRFieldWorkers.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function mapLink(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function HRFieldWorkers() {
  const [tab, setTab]               = useState('manage'); // 'manage' | 'log'
  const [employees, setEmployees]   = useState([]);
  const [search, setSearch]         = useState('');
  const [loadingEmp, setLoadingEmp] = useState(true);
  const [toggling, setToggling]     = useState(null);

  const [records, setRecords]       = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [month, setMonth]           = useState(new Date().getMonth() + 1);
  const [year, setYear]             = useState(new Date().getFullYear());

  useEffect(() => { fetchEmployees(); }, [search]);
  useEffect(() => { if (tab === 'log') fetchLocations(); }, [tab, month, year]);

  const fetchEmployees = async () => {
    try {
      setLoadingEmp(true);
      const url = search ? `${BASE}/fieldworkers/employees?search=${encodeURIComponent(search)}` : `${BASE}/fieldworkers/employees`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      setEmployees(data.employees || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEmp(false);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoadingLog(true);
      const res = await fetch(`${BASE}/fieldworkers/locations?month=${month}&year=${year}`, { headers: authHeaders() });
      const data = await res.json();
      setRecords(data.records || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLog(false);
    }
  };

  const toggleFieldWorker = async (emp) => {
    setToggling(emp.id);
    try {
      await fetch(`${BASE}/fieldworkers/${emp.id}/toggle`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ is_field_worker: !emp.is_field_worker }),
      });
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, is_field_worker: !e.is_field_worker } : e));
    } catch (err) {
      console.error(err);
    } finally {
      setToggling(null);
    }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y);

  const fieldWorkerCount = employees.filter(e => e.is_field_worker).length;

  return (
    <div className="fw-root">
      <div className="fw-header">
        <div>
          <h1 className="fw-title">📍 Field Workers</h1>
          <p className="fw-sub">Employees who work outside a fixed office — no location lock, full GPS visibility for HR</p>
        </div>
      </div>

      <div className="fw-tabs">
        <button className={`fw-tab ${tab === 'manage' ? 'fw-tab-active' : ''}`} onClick={() => setTab('manage')}>
          Manage ({fieldWorkerCount})
        </button>
        <button className={`fw-tab ${tab === 'log' ? 'fw-tab-active' : ''}`} onClick={() => setTab('log')}>
          Location Log
        </button>
      </div>

      {tab === 'manage' ? (
        <>
          <div className="fw-search-wrap">
            <span className="fw-search-icon">🔍</span>
            <input
              className="fw-search"
              placeholder="Search name or ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loadingEmp ? (
            <div className="fw-loading"><div className="fw-spinner" /></div>
          ) : (
            <div className="fw-emp-list">
              {employees.map(emp => (
                <div key={emp.id} className={`fw-emp-row ${emp.is_field_worker ? 'fw-emp-row-active' : ''}`}>
                  <div className="fw-emp-av" style={{ background: getAvatarColor(emp.full_name) }}>
                    {getInitials(emp.full_name)}
                  </div>
                  <div className="fw-emp-info">
                    <div className="fw-emp-name">{emp.full_name}</div>
                    <div className="fw-emp-meta">{emp.employee_id} · {emp.department || 'Unassigned'} · {emp.location_name}</div>
                  </div>
                  <button
                    className={`fw-toggle ${emp.is_field_worker ? 'fw-toggle-on' : ''}`}
                    onClick={() => toggleFieldWorker(emp)}
                    disabled={toggling === emp.id}
                  >
                    <span className="fw-toggle-knob" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="fw-period-row">
            <select className="fw-period-select" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select className="fw-period-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {loadingLog ? (
            <div className="fw-loading"><div className="fw-spinner" /></div>
          ) : records.length === 0 ? (
            <div className="fw-empty">
              <div className="fw-empty-icon">📍</div>
              <div className="fw-empty-title">No records yet</div>
              <div className="fw-empty-sub">
                {fieldWorkerCount === 0
                  ? 'Mark employees as field workers in the Manage tab first'
                  : `No clock-ins recorded for ${MONTHS[month-1]} ${year}`}
              </div>
            </div>
          ) : (
            <div className="fw-log-list">
              {records.map(r => (
                <div key={r.id} className="fw-log-card">
                  <div className="fw-log-top">
                    <div className="fw-log-av" style={{ background: getAvatarColor(r.full_name) }}>
                      {getInitials(r.full_name)}
                    </div>
                    <div className="fw-log-info">
                      <div className="fw-log-name">{r.full_name}</div>
                      <div className="fw-log-meta">{r.emp_code} · {new Date(r.date).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                    </div>
                  </div>
                  <div className="fw-log-points">
                    <div className="fw-log-point">
                      <span className="fw-log-point-label">🟢 Clock In</span>
                      <span className="fw-log-point-time">{formatTime(r.clock_in)}</span>
                      {r.clock_in_lat && r.clock_in_lng ? (
                        <a className="fw-map-link" href={mapLink(r.clock_in_lat, r.clock_in_lng)} target="_blank" rel="noopener noreferrer">
                          📍 View on Map
                        </a>
                      ) : <span className="fw-no-gps">No GPS recorded</span>}
                    </div>
                    <div className="fw-log-point">
                      <span className="fw-log-point-label">🔴 Clock Out</span>
                      <span className="fw-log-point-time">{formatTime(r.clock_out)}</span>
                      {r.clock_out_lat && r.clock_out_lng ? (
                        <a className="fw-map-link" href={mapLink(r.clock_out_lat, r.clock_out_lng)} target="_blank" rel="noopener noreferrer">
                          📍 View on Map
                        </a>
                      ) : <span className="fw-no-gps">{r.clock_out ? 'No GPS recorded' : '—'}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}